import React, { useState } from 'react';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { Stack, styled } from '@mui/system';

import AppBar from '@mui/material/AppBar';
import {
    learnMoreMenuItemConfig,
    userActionItems
} from '../menus/menuItemsConfig';
import { useAuth0 } from '@auth0/auth0-react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    padding: 4,
    backgroundColor: theme.palette.background.header,
    overflow: 'hidden',
    position: 'fixed',
    top: 'auto',
    bottom: 0,
    zIndex: 1000
}));

/**
 * Renders a menu item for the mobile bottom bar including icon and title
 * The item can either open a dialog specified in its config object or be given onClick functionality instead.
 *
 * @component
 * @param {Object} itemConfig - configuration for the menu item
 * @param {Boolean} disabled - true if the item should be disabled
 * @param {Boolean} isDialog - true if the item opens a dialog component
 * @param {Function} onClick - onClick functionality for the item, ignored if the item opens a dialog
 * @returns {React.Element}
 */
const MobileBarMenuItem = ({
    itemConfig,
    disabled = false,
    isDialog = false,
    onClick
}) => {
    const theme = useTheme();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleMenuItemClick = () => {
        isDialog ? setIsDialogOpen(true) : onClick();
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    return (
        <>
            <MenuItem
                onClick={handleMenuItemClick}
                disabled={disabled}
                sx={{ flexDirection: 'column' }}
                data-analyticsid={itemConfig.dataAnalyticsId}
            >
                <ListItemIcon
                    sx={{ minWidth: 'auto', justifyContent: 'center' }}
                >
                    <itemConfig.IconComponent.filled
                        sx={{
                            fontSize: '1.8rem',
                            fill: theme.palette.common.white
                        }}
                    />
                </ListItemIcon>
                <ListItemText
                    primaryTypographyProps={{
                        fontSize: '0.75rem',
                        color: theme.palette.common.white
                    }}
                >
                    {itemConfig.title.split(' ')[0]}
                </ListItemText>
            </MenuItem>
            {isDialog && (
                <itemConfig.DialogComponent
                    open={isDialogOpen}
                    handleFormClose={handleCloseDialog}
                    handleSubmissionViewClose={handleCloseDialog}
                />
            )}
        </>
    );
};

MobileBarMenuItem.propTypes = {
    itemConfig: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    isDialog: PropTypes.bool,
    onClick: PropTypes.func
};

/**
 * Renders a static bottom (footer) menu bar used for mobile screen sizes to provide additional features to the user
 *
 * @component
 * @returns {React.Element}
 */
export const MobileBottomBar = () => {
    const { isAuthenticated } = useAuth0();

    /* Function to handle Learn More menu item click */
    const handleLearnMoreClick = () => {
        FreshworksWidget('open');
    };

    return (
        <StyledAppBar data-analyticsid='bottom-bar'>
            <Stack direction='row' justifyContent='space-around'>
                {userActionItems.map((item, index) => (
                    <MobileBarMenuItem
                        key={item.id}
                        itemConfig={item}
                        disabled={!isAuthenticated}
                        isDialog
                    />
                ))}
                <MobileBarMenuItem
                    itemConfig={learnMoreMenuItemConfig}
                    onClick={handleLearnMoreClick}
                />
            </Stack>
        </StyledAppBar>
    );
};
